import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSupabaseUrl } from '../lib/environments';

interface InviterData {
  full_name: string;
  avatar_url: string | null;
}

export const ConnectInvite: React.FC = () => {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const [inviter, setInviter] = useState<InviterData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        const supabaseUrl = getSupabaseUrl();
        const res = await fetch(
          `${supabaseUrl}/functions/v1/share-invite-page?uid=${uid}&format=json`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.full_name) {
            setInviter(data);
          }
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [uid]);

  const handleJoin = () => {
    const params = new URLSearchParams();
    if (uid) params.set('connect_to', uid);
    if (inviter?.full_name) params.set('from', inviter.full_name);
    navigate(`/signup?${params.toString()}`);
  };

  const inviterName = inviter?.full_name || 'Someone';
  const initial = inviterName.charAt(0).toUpperCase();

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {inviter?.avatar_url ? (
          <img
            src={inviter.avatar_url}
            alt={inviterName}
            style={styles.avatar}
          />
        ) : (
          <div style={styles.avatarPlaceholder}>
            <span>{initial}</span>
          </div>
        )}
        <h1 style={styles.title}>{inviterName} invited you to Kizu</h1>
        <button onClick={handleJoin} style={styles.button}>
          Join Kizu
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    background: '#0a0a0a',
    color: '#fff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    textAlign: 'center',
    maxWidth: 400,
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    objectFit: 'cover',
    marginBottom: 20,
    border: '3px solid #333',
  },
  avatarPlaceholder: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    background: '#222',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 36,
    marginBottom: 20,
    border: '3px solid #333',
    color: '#fff',
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 24,
  },
  button: {
    display: 'block',
    width: '100%',
    background: '#4A90D9',
    color: '#fff',
    padding: '16px 40px',
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    fontSize: 18,
    fontWeight: 600,
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #333',
    borderTop: '3px solid #4A90D9',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto',
  },
};

export default ConnectInvite;
